import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID, HostListener, ElementRef, ViewChild } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { StudentJobService } from '../../../core/services/student-job-management/student-job.service';
import { HttpClientModule } from '@angular/common/http';
import { DeepLinkUtil } from '../../../core/utils/deep-link.util';

@Component({
  selector: 'app-featured-jobs',
  standalone: true,
  imports: [CommonModule, HttpClientModule, RouterModule],
  templateUrl: './featured-jobs.component.html',
  styleUrls: ['./featured-jobs.component.scss']
})
export class FeaturedJobsComponent implements OnInit, OnDestroy {
  jobs: any[] = [];
  isLoading = false;
  isFetchingMore = false;
  currentPage = 1;
  hasMoreJobs = true;
  isAtStart = true;
  isAtEnd = false;

  private readonly PAGE_SIZE = 6;
  private subscription = new Subscription();
  private scrollTimeout: any = null;
  private readonly SCROLL_DEBOUNCE_MS = 150;

  isBrowser: boolean;
  activeShareDropdown: string | null = null;
  @ViewChild('jobsGrid') jobsGrid!: ElementRef;

  constructor(
    private router: Router,
    private studentJobService: StudentJobService,
    @Inject(PLATFORM_ID) platformId: Object,
    private elementRef: ElementRef
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.activeShareDropdown = null;
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (!this.isBrowser) return;
    this.debouncedScrollCheck();
  }

  ngOnInit() {
    this.fetchJobs();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
  }

  fetchJobs() {
    this.isLoading = true;
    this.currentPage = 1;
    this.hasMoreJobs = true;
    this.jobs = [];

    this.subscription.add(
      this.studentJobService.getAllStudentsJobs(
        this.currentPage, this.PAGE_SIZE, 'all', undefined, undefined, { is_visible: true }
      ).subscribe({
        next: (res: any) => {
          if (res?.Success && res?.Data) {
            this.jobs = res.Data.jobs || [];
            this.hasMoreJobs = this.calculateHasMore(res.Data);
          } else {
            this.jobs = [];
            this.hasMoreJobs = false;
          }
          this.isLoading = false;
          setTimeout(() => this.checkScrollPosition(), 100);
        },
        error: () => {
          this.isLoading = false;
          this.jobs = [];
          this.hasMoreJobs = false;
        }
      })
    );
  }

  loadNextPage() {
    if (!this.hasMoreJobs || this.isFetchingMore || this.isLoading) return;
    this.isFetchingMore = true;
    const nextPage = this.currentPage + 1;

    this.subscription.add(
      this.studentJobService.getAllStudentsJobs(
        nextPage, this.PAGE_SIZE, 'all', undefined, undefined, { is_visible: true }
      ).subscribe({
        next: (res: any) => {
          if (res?.Success && res?.Data?.jobs) {
            const newJobs: any[] = res.Data.jobs;
            if (newJobs.length > 0) {
              const existingIds = new Set(this.jobs.map(j => j.job_id));
              const uniqueNewJobs = newJobs.filter(j => !existingIds.has(j.job_id));
              if (uniqueNewJobs.length > 0) {
                this.jobs = [...this.jobs, ...uniqueNewJobs];
              }
            }
            this.currentPage = nextPage;
            this.hasMoreJobs = this.calculateHasMore(res.Data);
          } else {
            this.hasMoreJobs = false;
          }
          this.isFetchingMore = false;
          setTimeout(() => this.checkScrollPosition(), 100);
        },
        error: () => {
          this.isFetchingMore = false;
        }
      })
    );
  }

  private calculateHasMore(data: any): boolean {
    if (!data) return false;
    if (data.total_pages && this.currentPage >= data.total_pages) return false;
    if (!data.jobs || data.jobs.length < this.PAGE_SIZE) return false;
    return true;
  }

  navigateToJob(jobId: string) {
    if (!jobId) return;
    // Deep Linking: If on Android, seamlessly open the app to this job instead of routing on the web.
    if (DeepLinkUtil.isAndroid()) {
        window.location.href = DeepLinkUtil.getAndroidIntentUrl(`job/${jobId}`);
        return;
    }
    this.router.navigate(['/job', jobId]);
  }

  getTimeAgo(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1d ago';
    return diffDays + 'd ago';
  }

  saveJob(job: any, event: Event) {
    event.stopPropagation();
    if (!job?.job_id) return;
    if (this.isBrowser) {
      localStorage.setItem('pending_save_job_id', job.job_id);
    }
    this.router.navigate(['/login'], {
      queryParams: { redirectUrl: '/student-dashboard/jobs/saved' }
    });
  }

  async shareJob(job: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isBrowser || !job?.job_id) return;

    const url = window.location.origin + '/job/' + job.job_id;
    const shareData = {
      title: `${job.job_title} at ${job.company_name} - BeExpert`,
      text: `Check out this amazing opportunity: ${job.job_title} at ${job.company_name}!`,
      url: url
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        this.toggleShareFallback(job.job_id);
      }
    } catch (err) {
    }
  }

  toggleShareFallback(jobId: string) {
    if (this.activeShareDropdown === jobId) {
      this.activeShareDropdown = null;
    } else {
      this.activeShareDropdown = jobId;
    }
  }

  shareTo(platform: string, job: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isBrowser || !job?.job_id) return;

    const url = encodeURIComponent(window.location.origin + '/job/' + job.job_id);
    const text = encodeURIComponent(`Check out this amazing opportunity: ${job.job_title} at ${job.company_name}!`);

    let shareUrl = '';
    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${text}%20${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${url}&text=${text}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent('Amazing Job Opportunity')}&body=${text}%20${url}`;
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
    this.activeShareDropdown = null;
  }

  async copyLink(job: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isBrowser || !job?.job_id) return;
    try {
      const url = window.location.origin + '/job/' + job.job_id;
      await navigator.clipboard.writeText(url);
      const copyBtn = document.getElementById('copy-btn-' + job.job_id);
      if (copyBtn) {
        const originalText = copyBtn.innerHTML;
        copyBtn.innerHTML = '<span class="text-emerald-600 font-medium">Copied!</span>';
        setTimeout(() => copyBtn.innerHTML = originalText, 2000);
      }
    } catch (err) {
    }
  }

  trackByJobId(index: number, job: any): string {
    return job?.job_id || index.toString();
  }

  getCardScrollAmount(): number {
    if (!this.jobsGrid?.nativeElement || !this.isBrowser) return 300;
    const cards = this.jobsGrid.nativeElement.querySelectorAll('.job-card');
    if (cards.length >= 2) {
      const diff = cards[1].offsetLeft - cards[0].offsetLeft;
      return diff > 0 ? diff : 300;
    } else if (cards.length === 1) {
      return cards[0].offsetWidth + 24;
    }
    return 300;
  }

  scrollLeft() {
    if (!this.jobsGrid?.nativeElement || !this.isBrowser) return;
    const scrollAmount = this.getCardScrollAmount();
    this.jobsGrid.nativeElement.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    setTimeout(() => this.checkScrollPosition(), 400);
  }

  scrollRight() {
    if (!this.jobsGrid?.nativeElement || !this.isBrowser) return;
    const scrollAmount = this.getCardScrollAmount();
    this.jobsGrid.nativeElement.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    setTimeout(() => this.checkScrollPosition(), 400);
  }

  handleCardClick(event: Event) {
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) {
      return;
    }
    this.scrollRight();
  }

  onGridScroll() {
    this.debouncedScrollCheck();
  }

  private debouncedScrollCheck() {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = setTimeout(() => {
      this.checkScrollPosition();
      this.scrollTimeout = null;
    }, this.SCROLL_DEBOUNCE_MS);
  }

  checkScrollPosition() {
    if (!this.jobsGrid?.nativeElement || !this.isBrowser) return;
    const el = this.jobsGrid.nativeElement;

    this.isAtStart = el.scrollLeft <= 5;

    const maxScroll = el.scrollWidth - el.clientWidth;
    this.isAtEnd = maxScroll <= 0 || el.scrollLeft >= maxScroll - 5;

    if (el.scrollLeft >= maxScroll - 100 && this.hasMoreJobs && !this.isFetchingMore && !this.isLoading) {
      this.loadNextPage();
    }
  }
}
